import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Video, Activity, AlertTriangle, MessageSquare,
  RefreshCw, ArrowRight,
} from 'lucide-react';
import MetricCard from '../components/MetricCard';
import { api } from '../services/api';
import type { AdminMetrics } from '../types';
import { fmtDate } from '../utils/format';

export default function DashboardPage() {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/metrics')
      .then(r => setMetrics(r.data.metrics))
      .catch(() => setError('Failed to load metrics'))
      .finally(() => setLoading(false));
  }, []);

  const QUICK_LINKS = [
    { to: '/jobs',      label: 'Job Monitor',       desc: 'View all background jobs across every org', Icon: Activity },
    { to: '/errors',    label: 'Error Log',          desc: 'Failed jobs and processing errors',        Icon: AlertTriangle },
    { to: '/accounts',  label: 'Accounts',           desc: 'Browse all organizations and usage',       Icon: Building2 },
    { to: '/reprocess', label: 'Meeting Reprocess',  desc: 'Fix zero-extraction meetings for any org', Icon: RefreshCw },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Platform Overview</h1>
        {metrics && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            Last updated {fmtDate(metrics.generatedAt)}
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: 'var(--danger-subtle)', border: '1px solid var(--danger)', borderRadius: 8, marginBottom: 20, fontSize: 13, color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      {/* Metrics grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14, marginBottom: 32 }}>
        <MetricCard
          label="Total Orgs"
          value={loading ? '…' : (metrics?.totalOrgs ?? 0)}
          Icon={Building2}
          color="var(--accent)"
        />
        <MetricCard
          label="Total Users"
          value={loading ? '…' : (metrics?.totalUsers ?? 0)}
          Icon={Users}
          color="#60a5fa"
        />
        <MetricCard
          label="Meetings This Month"
          value={loading ? '…' : (metrics?.totalMeetingsThisMonth ?? 0)}
          Icon={Video}
          color="var(--success)"
        />
        <MetricCard
          label="Running Jobs"
          value={loading ? '…' : (metrics?.runningJobs ?? 0)}
          Icon={Activity}
          color="var(--warning)"
          pulse={(metrics?.runningJobs ?? 0) > 0}
        />
        <MetricCard
          label="Error Rate (24h)"
          value={loading ? '…' : `${metrics?.errorRate ?? '0.0'}%`}
          Icon={AlertTriangle}
          color={(parseFloat(metrics?.errorRate ?? '0') > 5) ? 'var(--danger)' : 'var(--text-dim)'}
          subtext={`${metrics?.failedJobs ?? 0} failed / ${metrics?.totalJobs ?? 0} total`}
        />
        <MetricCard
          label="Escalated Feedback"
          value={loading ? '…' : (metrics?.escalatedFeedback ?? 0)}
          Icon={MessageSquare}
          color={(metrics?.escalatedFeedback ?? 0) > 0 ? 'var(--waiting)' : 'var(--text-dim)'}
        />
      </div>

      {/* Quick links */}
      <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 12, letterSpacing: '0.05em' }}>
        QUICK ACTIONS
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
        {QUICK_LINKS.map(({ to, label, desc, Icon }) => (
          <div
            key={to}
            onClick={() => navigate(to)}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '16px 18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
          >
            <Icon size={18} color="var(--accent)" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{desc}</div>
            </div>
            <ArrowRight size={14} color="var(--text-dim)" />
          </div>
        ))}
      </div>
    </div>
  );
}
